// @ts-strict-ignore
import { LMModelServiceAdapter } from './lm-model-service-adapter';
import { LMModelCache, LMModelStatusEvent } from './lm-model';
import { Subject } from 'rxjs';

export class LMModelState {

  private _dirtyFields: LMDirtyFields;
  private _errorFields: LMErrorFields;
  private _blockTransactionFields: LMBlockingErrorFields;
  private _statusChanges$ = new Subject<LMModelStatusEvent>();

  constructor(private lmModelServiceAdapter: LMModelServiceAdapter) {
    this._clearDirtyFields();
    this._clearErrorFields();
  }

  // public getters / setters
  public set cache(modelCache: LMModelCache) {
    this._dirtyFields = modelCache.dirtyFields;
    this._errorFields = modelCache.errorFields;
  }

  public get dirtyFields(): LMDirtyFields {
    return this._dirtyFields;
  }

  public get errorFields(): LMErrorFields {
    return this._errorFields;
  }

  public get blockingErrorFields(): LMBlockingErrorFields {
    return this._blockTransactionFields;
  }

  public get hasError(): boolean {
    return (Object.keys(this._errorFields).some(key => this._errorFields[ key ].hasError === true));
  }

  public get hasBlockingError(): boolean {
    return (Object.keys(this._blockTransactionFields).some(key => this._blockTransactionFields[ key ].hasError === true));
  }

  public get isDirty(): boolean {
    return (Object.keys(this._dirtyFields).some(key => this._dirtyFields[ key ] === true));
  }

  public get statusChanges(): Subject<LMModelStatusEvent> {
    return this._statusChanges$;
  }

  // private methods
  private _clearDirtyFields() {
    this._dirtyFields = {};
  }

  private _clearErrorFields() {
    this._errorFields = {};
    this._blockTransactionFields = {};
  }

  private _sendStatus(cause?: LMModelStatusCause) {
    this._statusChanges$.next(
      new LMModelStatusEvent(this.isDirty, this.hasError, cause));
  }

  // public methods
  public clearAll() {
    this._clearDirtyFields();
    this._clearErrorFields();
  }

  public destroy() {
    this._statusChanges$.unsubscribe();
    this._statusChanges$ = null;
  }

  public isFieldDirty(bindId: string): boolean {
    return !(Object.keys(this._dirtyFields).find(fieldId => fieldId === bindId) === undefined);
  }

  public setDirty(bindId: string, isDirty: boolean) {
    if (isDirty) {
      this._dirtyFields[ bindId ] = isDirty;
    } else {
      delete this._dirtyFields[ bindId ];
    }

    this._sendStatus({ bindId, isDirty });
  }

  public setError(bindId: string, hasError: boolean, message: string) {
    if (hasError) {
      this._errorFields[ bindId ] = {
        hasError,
        message
      };
    } else {
      delete this._errorFields[ bindId ];
    }

    this._sendStatus({ bindId, hasError });
  }

  // This is to be used in conjunction with set Error, when we have a field in
  // error that we want to block the save/submit buttons
  public setBlockTransactionError(bindId: string, hasError: boolean, invalidEvent: any) {
    if (hasError) {
      this._blockTransactionFields[ bindId ] = {
        hasError,
        message: 'blockTransaction',
        invalidFieldEvent: invalidEvent
      };
    } else {
      delete this._blockTransactionFields[ bindId ];
    }
  }
}
